import { CircleSlash, Target } from "lucide-react";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { LoadingRows } from "@/components/shared/loading-rows";
import { StatusPill } from "@/components/shared/status-badge";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { type CapabilityStatus, usePersonCapabilityGaps } from "@/hooks/use-capability-gaps";

/**
 * Where one person stands against what their role asks of them — EPIC-023,
 * WI-042.
 *
 * **Only rated capabilities are listed.** A required capability nobody has
 * scored is left out entirely rather than drawn as a blank row, so the tab
 * reads as a list of findings. The one acknowledgement of what is missing is
 * the "N of M rated" line — without it the page would imply the role asks
 * for only the handful shown.
 */

const STATUS_META: Record<CapabilityStatus, { label: string; tone: "success" | "warning" }> = {
  meets: { label: "Meets", tone: "success" },
  below: { label: "Below", tone: "warning" },
};

export function PersonCapabilityGaps({ personId }: { personId: string }) {
  const { data, isLoading, isError, error, refetch } = usePersonCapabilityGaps(personId);

  if (isLoading) return <LoadingRows />;
  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Couldn't load this person's capabilities"}
        onRetry={() => refetch()}
      />
    );
  }
  if (!data) return null;

  if (!data.roleId) {
    return (
      <EmptyState
        icon={CircleSlash}
        title="No role assigned"
        description="Capabilities are measured against the role someone holds, so there is nothing to compare until they have one."
      />
    );
  }

  const { meets, below, rated, required } = data.summary;

  if (rated === 0) {
    return (
      <EmptyState
        icon={Target}
        title="Nothing rated yet"
        description={
          required > 0
            ? `${data.roleName} asks for ${required} ${required === 1 ? "capability" : "capabilities"}. None of them has been rated in a closed review round, so there is nothing to show here yet.`
            : `${data.roleName} has no required skills or competencies recorded, so there is nothing to measure against.`
        }
      />
    );
  }

  return (
    <DetailSection
      title="Against their role"
      boldTitle
      className="gap-3 py-4"
      headerActions={
        <div className="flex items-center gap-1.5">
          <StatusPill tone="success" label={`${meets} meets`} />
          <StatusPill tone="warning" label={`${below} below`} />
        </div>
      }
    >
      <p className="text-muted-foreground text-xs">
        {data.roleName}
        {data.roleLevelName ? ` — ${data.roleLevelName}` : ""}. {rated} of {required} required{" "}
        {required === 1 ? "capability has" : "capabilities have"} been rated in a closed review
        round; the rest aren't shown.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground text-xs">
              <th className="px-3 py-2 font-medium">Competency</th>
              <th className="px-3 py-2 font-medium text-right">Score</th>
              <th className="px-3 py-2 font-medium">Standing</th>
              <th className="hidden px-3 py-2 font-medium lg:table-cell">
                What “meets” looks like
              </th>
            </tr>
          </thead>
          <tbody>
            {data.capabilities.map((row) => (
              <tr key={row.id} className="border-b last:border-b-0">
                <td className="px-3 py-2.5 font-medium text-foreground">
                  {row.name}
                  {row.categoryName && (
                    <span className="block text-2xs text-muted-foreground">{row.categoryName}</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <SimpleTooltip
                    content={`Average of ${row.cycleCount} closed ${
                      row.cycleCount === 1 ? "round" : "rounds"
                    }. Meets at ${data.meetsAtOrdinal}.`}
                  >
                    <span className="cursor-help font-mono tabular-nums">
                      {row.averageOrdinal.toFixed(1)}
                    </span>
                  </SimpleTooltip>
                </td>
                <td className="px-3 py-2.5">
                  <StatusPill
                    tone={STATUS_META[row.status].tone}
                    label={STATUS_META[row.status].label}
                  />
                </td>
                <td className="hidden max-w-md px-3 py-2.5 text-muted-foreground lg:table-cell">
                  {row.expectation ?? <span className="text-muted-foreground/60">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DetailSection>
  );
}
